<template>
  <span class="children:full">
    <span v-if="shouldRenderTextLink">
      <vf-link
        v-style="{ c: textLinkColor, display: textLinkDisplay }"
        :aria-label="ariaLabel ?? (iconOnly ? text : undefined)"
        class="c-$c space-x-1"
        :class="[
          { '@hover:c-$ch': textLinkHoverColor },
          textLinkStyle,
        ]"
        :data-cta="delocalizedTracking"
        :style="{ '--ch': textLinkHoverColor }"
        :target="computedTargetAttribute"
        :to="fullUrl"
        :variant="textLinkUnderline === 'never' ? 'quieter' : (textLinkUnderline === 'hover' ? 'quiet' : undefined)"
        @click="clickHandler"
      >
        <vf-icon
          v-if="icon && iconPosition !== 'right'"
          class="align-text-bottom i-block"
          :dir="icon.dir"
          :name="icon.icon"
          :size="iconSize"
        />
        <span :class="{ 'sr-only': iconOnly }">
          {{ text }}
        </span>
        <vf-icon
          v-if="icon && iconPosition === 'right'"
          class="align-text-bottom i-block"
          :dir="icon.dir"
          :name="icon.icon"
          :size="iconSize"
        />
      </vf-link>
    </span>

    <vf-button
      v-if="shouldRenderButton"
      v-style:display="buttonDisplay"
      :aria-label="ariaLabel ?? (iconOnly ? text : undefined)"
      :class="classOverride"
      :class-content="[{ 'flex-row-reverse': iconPosition === 'right' }, 'gap-1']"
      :data-cta="delocalizedTracking"
      :disabled
      :size="buttonSize"
      :target="computedTargetAttribute"
      :to="fullUrl"
      :variant="style"
      @click="clickHandler"
    >
      <vf-icon v-if="icon" :dir="icon.dir" :name="icon.icon" :size="iconSize" />
      <span :class="{ 'sr-only': iconOnly }">
        {{ text }}
      </span>
    </vf-button>
  </span>
</template>

<script lang="ts" setup>
import type { Responsive } from '#types/common'
import type { ButtonSize } from '#types/components/vf/button'
import type { Target } from '#types/target'

const {
  actionLink,
  ariaLabel,
  behavior,
  classOverride,
  delocalizedTracking,
  disabled,
  icon,
  iconPosition,
  iconSize = 'md',
  size = {} as Responsive<{ size?: ButtonSize, fullWidth?: boolean }>,
  style,
  targetAttribute,
  type,
  text,
  url = '',
} = defineProps<Target & { disabled?: boolean }>()

const route = useRoute()
const router = useRouter()
const { getActionLinkHandler } = useCmsActionLink(() => {
  if (route.path.includes('/p/')) return 'PDP'
  if (route.path.includes('/c/')) return 'PLP'
  return 'cms:button'
})

const shouldRenderTextLink = computed(() => Object.values(style || {}).filter(Boolean).includes('text-link'))
const shouldRenderButton = computed(() => Object.values(style || {}).filter(Boolean).some((val) => val !== 'text-link'))

const textLinkDisplay = computed(() => Object.entries(style || {}).reduce((acc, [key, value]) => {
  acc[key] = value === 'text-link' ? 'inline' : 'none'
  return acc
}, {}))

const buttonDisplay = computed(() => Object.entries(style || {}).reduce((acc, [key, value]) => {
  acc[key] = value !== 'text-link' ? 'inline-flex' : 'none'
  return acc
}, {}))

const clickHandler = getActionLinkHandler({ actionLink, behavior, targetAttribute, url, text, ariaLabel })

const fullUrl = computed(() => {
  // Action links should not have any anchors
  if (clickHandler || type === 'VfCanvasActionLink') return undefined

  if (url.startsWith('#modal')) {
    return `${route.fullPath.toString()}${url}`
  }
  else if (url.startsWith('?') && url.includes('#modal')) {
    const [urlQuery, urlHash] = url.substring(1).split('#')
    const additionalParams = Object.fromEntries((new URLSearchParams(urlQuery)).entries())
    return router.resolve({
      query: { ...route.query, ...additionalParams },
      hash: urlHash ? `#${urlHash}` : ''
    }).href
  }
  return url
})

const computedTargetAttribute = computed(() => type === 'CMDownload' ? (targetAttribute || '_self') : targetAttribute)

const { sm: { size: sm = 'sm' } = {}, md, lg } = size

const buttonSize: ButtonSize | Responsive<ButtonSize> = {
  sm,
  md: md?.size,
  lg: lg?.size
}
</script>
